'use client';

import noop from 'lodash/noop';
import { LazyMotion, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useIsomorphicLayoutEffect } from '../../../hooks/use-isomorphic-layout-effect';
import { loadDomAnimation } from '../../../lib/motion';
import { cn } from '../../../lib/utils';
import { Drawer, DrawerBody } from '../../drawer/drawer';
import { MENU_PANEL_DURATION, MENU_PANEL_EASE } from '../shared/menu-motion';
import { MenuSizeProvider } from '../shared/menu-size-context';
import {
  DRAWER_MENU_ROOT_PANEL,
  DrawerMenuPanelsProvider,
  DrawerMenuProvider,
  useDrawerMenuContext,
  useDrawerMenuPanelsContext,
  type DrawerMenuContextValue,
  type DrawerMenuPanelEntry,
  type DrawerMenuPanelsContextValue,
} from './drawer-menu-context';

// vaul's slide-out, the window in which a closing sheet is still on screen
const DRAWER_CLOSE_MS = 500;

// how far panels travel, as a percentage of the sheet width
const PANEL_TRAVEL = 40;

// the panel transition, plus a moment for focus to settle
const SWAP_SETTLE_MS = MENU_PANEL_DURATION * 1000 + 100;

const REDUCED_TRANSITION = { duration: 0.12, ease: 'easeOut' } as const;
const PANEL_TRANSITION = { duration: MENU_PANEL_DURATION, ease: MENU_PANEL_EASE } as const;

const ROOT_STACK: DrawerMenuPanelEntry[] = [{ id: DRAWER_MENU_ROOT_PANEL }];

const MENU_SIZE = { size: 'standard' } as const;

type PanelHeight = { value: number; animated: boolean };

/** How the sheet closed: a row or footer button, or anything that dismisses it. */
export type DrawerMenuCloseReason = 'item-press' | 'dismiss';

export type DrawerMenuProps = {
  /** The sheet is always controlled: the element that opens it lives in the consumer. */
  open: boolean;
  /**
   * Called when the sheet opens or closes itself. A close carries why it happened,
   * so a filter sheet can discard its draft on `dismiss` and keep it on `item-press`.
   * A consumer closing the sheet through `open` does not go through here.
   */
  onOpenChange?: (open: boolean, reason?: DrawerMenuCloseReason) => void;
  /** Called after any close finishes. Async rows reset their visuals here. */
  onCloseComplete?: () => void;
  className?: string;
  children?: ReactNode;
};

/**
 * Bottom-sheet action menu: a `Drawer` holding menu rows, with submenus opening as
 * sliding panels. Rows are plain buttons, so the keyboard is the dialog's own.
 * Children render inside the sheet; the element that opens it lives in the consumer.
 */
export const DrawerMenu = ({
  open,
  onOpenChange,
  onCloseComplete,
  className,
  children,
}: DrawerMenuProps) => {
  const [stack, setStack] = useState<DrawerMenuPanelEntry[]>(ROOT_STACK);
  const [outgoingPanel, setOutgoingPanel] = useState<string | null>(null);

  const wasOpenRef = useRef(open);
  const closeCompleteRef = useRef(onCloseComplete);

  useEffect(() => {
    closeCompleteRef.current = onCloseComplete;
  });

  const setOpen = useCallback(
    (next: boolean, reason?: DrawerMenuCloseReason) => onOpenChange?.(next, reason),
    [onOpenChange],
  );

  // motion skips onAnimationComplete on an interrupted animation, so the swap also ends on a timer
  useEffect(() => {
    if (outgoingPanel === null) {
      return;
    }

    const timer = setTimeout(() => setOutgoingPanel(null), MENU_PANEL_DURATION * 1000);

    return () => clearTimeout(timer);
  }, [outgoingPanel]);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return;
    }

    if (!wasOpenRef.current) {
      return;
    }

    wasOpenRef.current = false;

    // panels and async row visuals only settle once the sheet is off screen
    const timer = setTimeout(() => {
      setStack(ROOT_STACK);
      setOutgoingPanel(null);
      closeCompleteRef.current?.();
    }, DRAWER_CLOSE_MS);

    return () => clearTimeout(timer);
  }, [open]);

  const contextValue = useMemo<DrawerMenuContextValue>(
    () => ({
      closeFromItem: () => setOpen(false, 'item-press'),
      stack,
      pushPanel: (entry) => {
        setOutgoingPanel(stack[stack.length - 1]?.id ?? null);
        setStack((prev) => [...prev, entry]);
      },
      popPanel: () => {
        if (stack.length < 2) {
          return;
        }

        setOutgoingPanel(stack[stack.length - 1]?.id ?? null);
        setStack((prev) => prev.slice(0, -1));
      },
      outgoingPanel,
      endTransition: () => setOutgoingPanel(null),
    }),
    [stack, outgoingPanel, setOpen],
  );

  return (
    <DrawerMenuProvider value={contextValue}>
      <MenuSizeProvider value={MENU_SIZE}>
        <Drawer
          isOpen={open}
          onOpenChange={(next) => setOpen(next, next ? undefined : 'dismiss')}
          className={className}>
          {children}
        </Drawer>
      </MenuSizeProvider>
    </DrawerMenuProvider>
  );
};

export type DrawerMenuContentProps = {
  className?: string;
  children?: ReactNode;
};

/**
 * The scrolling panel viewport. Place `DrawerMenuHeader` before it and
 * `DrawerMenuFooter` after it, as siblings, so the header stays fixed and the footer
 * stays pinned while the rows scroll.
 */
export const DrawerMenuContent = ({ className, children }: DrawerMenuContentProps) => {
  const { stack, outgoingPanel } = useDrawerMenuContext();
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<PanelHeight>();
  const reduced = useReducedMotion() ?? false;
  const activeId = stack[stack.length - 1]?.id ?? DRAWER_MENU_ROOT_PANEL;

  /**
   * Scroll memory. Each panel keeps the offset it was left at, so opening a submenu and
   * coming back lands where the user was rather than at the top. It lasts as long as the
   * open sheet: the content unmounts on close, so a reopen starts fresh.
   */
  const scrollOffsets = useRef(new Map<string, number>());
  const lastActiveRef = useRef(activeId);
  const swappingRef = useRef(false);

  const getScroller = useCallback(
    () => viewport?.closest<HTMLElement>('[data-slot="drawer-body"]') ?? null,
    [viewport],
  );

  // flagged before paint, so a swap is known by the time the clamp fires its scroll event
  useIsomorphicLayoutEffect(() => {
    swappingRef.current = outgoingPanel !== null;
  }, [outgoingPanel]);

  // a swap clamps the offset, so only real scrolling is remembered
  useEffect(() => {
    const scroller = getScroller();
    if (!scroller) return;
    const remember = () => {
      if (!swappingRef.current) {
        scrollOffsets.current.set(activeId, scroller.scrollTop);
      }
    };
    scroller.addEventListener('scroll', remember, { passive: true });
    return () => scroller.removeEventListener('scroll', remember);
  }, [activeId, getScroller]);

  useIsomorphicLayoutEffect(() => {
    if (lastActiveRef.current === activeId) return;
    lastActiveRef.current = activeId;

    const scroller = getScroller();
    const offset = scrollOffsets.current.get(activeId);

    if (!scroller || !offset) return;

    // an arriving panel grows and takes focus, both of which drop the offset
    let frame = 0;
    const until = performance.now() + SWAP_SETTLE_MS;

    const apply = () => {
      scroller.scrollTop = offset;
      if (performance.now() < until) {
        frame = requestAnimationFrame(apply);
      }
    };

    apply();

    return () => cancelAnimationFrame(frame);
  }, [activeId, getScroller]);

  const reportHeight = useCallback(
    (id: string, measured: number) => {
      if (id === activeId) {
        // the first measurement replaces `auto`, so it must not animate
        setHeight((prev) => ({ value: measured, animated: prev !== undefined }));
      }
    },
    [activeId],
  );

  const panelsContextValue = useMemo<DrawerMenuPanelsContextValue>(
    () => ({ viewport, reportHeight }),
    [viewport, reportHeight],
  );

  return (
    <DrawerBody className="min-h-0 max-w-none first:mt-0">
      <LazyMotion features={loadDomAnimation} strict>
        {/* a plain CSS tween: motion stops writing the height when a panel animation is
              interrupted, leaving the sheet at the wrong size */}
        <div
          ref={setViewport}
          data-slot="drawer-menu-viewport"
          className="relative overflow-hidden"
          style={{
            height: height?.value ?? 'auto',
            transition:
              reduced || !height?.animated
                ? undefined
                : `height ${MENU_PANEL_DURATION}s cubic-bezier(${MENU_PANEL_EASE.join(', ')})`,
          }}>
          <DrawerMenuPanelsProvider value={panelsContextValue}>
            <DrawerMenuPanel id={DRAWER_MENU_ROOT_PANEL} className={className}>
              {children}
            </DrawerMenuPanel>
          </DrawerMenuPanelsProvider>
        </div>
      </LazyMotion>
    </DrawerBody>
  );
};

export type DrawerMenuPanelProps = {
  id: string;
  className?: string;
  children?: ReactNode;
};

/**
 * One list of rows inside the viewport. The panel on screen sits at rest, the one it
 * came from slides the other way, and panels waiting off screen park to the side they
 * will return from: shallower than the active one to the left, deeper to the right.
 */
export const DrawerMenuPanel = ({ id, className, children }: DrawerMenuPanelProps) => {
  const { stack, outgoingPanel, endTransition } = useDrawerMenuContext();
  const { viewport, reportHeight } = useDrawerMenuPanelsContext();
  const reduced = useReducedMotion() ?? false;
  const nodeRef = useRef<HTMLDivElement>(null);

  const index = stack.findIndex((entry) => entry.id === id);
  const isActive = index !== -1 && index === stack.length - 1;
  const isTransitioning = outgoingPanel !== null;

  // a pushed panel renders for the first time mid-transition, so it starts off screen
  const initial = isTransitioning
    ? { transform: reduced ? 'translateX(0%)' : `translateX(${PANEL_TRAVEL}%)`, opacity: 0 }
    : false;

  useIsomorphicLayoutEffect(() => {
    const measured = nodeRef.current?.scrollHeight;

    if (isActive && measured) {
      reportHeight(id, measured);
    }
  }, [id, isActive, reportHeight, viewport, children]);

  if (!viewport || (index === -1 && id !== outgoingPanel)) {
    return null;
  }

  const parkedShift = reduced
    ? 'translateX(0%)'
    : `translateX(${index === -1 ? PANEL_TRAVEL : -PANEL_TRAVEL}%)`;

  return createPortal(
    <m.div
      ref={nodeRef}
      data-slot="drawer-menu-panel"
      className={cn(
        'flex flex-col gap-0.5 px-5 pb-2',
        isActive && !isTransitioning ? 'relative' : 'absolute inset-x-0 top-0',
        !isActive && 'pointer-events-none',
        !isActive && !isTransitioning && 'invisible',
        className,
      )}
      initial={initial}
      animate={
        isActive
          ? { transform: 'translateX(0%)', opacity: 1 }
          : { transform: parkedShift, opacity: 0 }
      }
      transition={reduced ? REDUCED_TRANSITION : PANEL_TRANSITION}
      // an onUpdate keeps the animation off WAAPI: in nested iframes (Storybook Docs) Chrome
      // stops painting a panel once its WAAPI animation finishes
      onUpdate={noop}
      onAnimationComplete={endTransition}>
      {children}
    </m.div>,
    viewport,
  );
};
